import { MenuButton as FluentMenuButton } from "@fluentui/web-components";
/**
 * MenuButton
 * @summary A MenuButton component that extends Fluent UI's MenuButton with custom styling and behavior.
 *
 * @example
 * ```html
 * <fabric-menu-button appearance="primary" shape="rounded" size="large">
 *   <span slot="start">🚀</span>
 *   Menu
 *   <span slot="end"></span>
 * </fabric-menu-button>
 * ```
 *
 * @attr {MenuButtonAppearances | undefined} appearance - Indicates the styled appearance of the button.
 * @attr {MenuButtonShape | undefined} shape - The shape of the button.
 * @attr {MenuButtonSize | undefined} size - The size of the button.
 * @attr {boolean | undefined} icon-only - Indicates that the button should only display as an icon with no text content.
 * @attr {boolean | undefined} autofocus - Indicates the button should be focused when the page is loaded.
 * @attr {boolean | undefined} disabled-focusable - Indicates that the button is focusable while disabled.
 * @attr {number | undefined} tabindex - Sets that the button tabindex attribute.
 * @attr {string | undefined} formaction - The URL that processes the form submission.
 * @attr {string | undefined} form - The id of a form to associate the element to.
 * @attr {string | undefined} formenctype - The encoding type for the form submission.
 * @attr {string | undefined} formmethod - The HTTP method that the browser uses to submit the form.
 * @attr {boolean | undefined} formnovalidate - Indicates that the form will not be validated when submitted.
 * @attr {string | undefined} formtarget - The target frame or window to open the form submission in.
 * @attr {string | undefined} name - The name of the element. This element's value will be surfaced during form submission under the provided name.
 * @attr {string | undefined} type - The button type.
 * @attr {string | undefined} value - The value attribute.
 *
 * @prop {MenuButtonAppearances | undefined} appearance - Indicates the styled appearance of the button.
 * @prop {MenuButtonShape | undefined} shape - The shape of the button.
 * @prop {MenuButtonSize | undefined} size - The size of the button.
 * @prop {boolean | undefined} iconOnly - Indicates that the button should only display as an icon with no text content.
 * @prop {boolean | undefined} autofocus - Indicates the button should be focused when the page is loaded.
 * @prop {boolean | undefined} disabledFocusable - Indicates that the button is focusable while disabled.
 * @prop {number | undefined} tabindex - Sets that the button tabindex attribute.
 * @prop {string | undefined} formaction - The URL that processes the form submission.
 * @prop {string | undefined} form - The id of a form to associate the element to.
 * @prop {string | undefined} formenctype - The encoding type for the form submission.
 * @prop {string | undefined} formmethod - The HTTP method that the browser uses to submit the form.
 * @prop {boolean | undefined} formnovalidate - Indicates that the form will not be validated when submitted.
 * @prop {string | undefined} formtarget - The target frame or window to open the form submission in.
 * @prop {string | undefined} name - The name of the element. This element's value will be surfaced during form submission under the provided name.
 * @prop {string | undefined} type - The button type.
 * @prop {string | undefined} value - The value attribute.
 *
 * @slot start - Content which can be provided before the button content.
 * @slot end - Content which can be provided after the button content.
 * @slot - The default slot for button content.
 *
 * @csspart content - The button content container.
 *
 * @cssprop --icon-spacing - Custom property defined in component styles
 *
 * @method keypressHandler - Handles keypress events for the button.
 * @method press - Programmatically presses the button.
 * @method resetForm - Resets the associated form.
 *
 * @extends FluentMenuButton
 * @tagname fabric-menu-button
 * @public
 */
export class MenuButton extends FluentMenuButton {
}
//# sourceMappingURL=menu-button.js.map